import { useState } from 'react';
import { Spin, Progress } from 'antd';
import classNames from 'classnames';
import { removeFileFromContexts } from 'stores/current-conversation';
import { FileType } from 'utils/file-utils';

import { ReactComponent as FileChitIconClose } from './file-chit-icons/icon-close.svg';
import { ReactComponent as FileChitIconPDF } from './file-chit-icons/icon-pdf.svg';
import { ReactComponent as FileChitIconPPTX } from './file-chit-icons/icon-pptx.svg';
import { ReactComponent as FileChitIconDOCX } from './file-chit-icons/icon-docx.svg';
import { ReactComponent as FileChitIconTXT } from './file-chit-icons/icon-txt.svg';
import { ReactComponent as FileChitIconXLSX } from './file-chit-icons/icon-xlsx.svg';

import './file-chit.css';

export function getFileTypeForChit(filename: string): [string, string] {
  const fileTypeMapping: { [key: string]: string } = {
    pptx: 'Powerpoint presentation',
    docx: 'Word document',
    txt: 'Text file',
    pdf: 'PDF',
    xlsx: 'Excel spreadsheet',
  };

  const fileExtension = filename?.split('.').pop()?.toLowerCase();

  if (fileExtension && fileTypeMapping[fileExtension]) {
    return [fileTypeMapping[fileExtension], fileExtension];
  } else {
    return ['Unknown file', 'unknown'];
  }
}

export const FileChit = ({ file, isEditable }) => {
  const fileIsEditable = isEditable;
  const fileIsProcessing = file.processingStatus === 'in progress';
  const fileIsUploading = file.processingStatus === 'uploading';
  const fileHasError = file.processingStatus === 'error';
  const [isRemoving, setIsRemoving] = useState(false);

  const [fileType, extension] = getFileTypeForChit(file.displayFileName);

  const getStatusLabel = (fileType: string) => {
    if (file?.processingFailureComment) {
      return file.processingFailureComment;
    }

    if (fileIsProcessing) {
      return 'Processing...';
    }

    if (fileIsUploading) {
      return 'Uploading...';
    }

    if (fileType !== 'Unknown file') {
      return fileType;
    }

    return 'Unknown file type';
  };

  const isFileProcessingComplete = () => {
    return !fileIsUploading && !fileIsProcessing;
  };

  const removeChitFromFilesSelected = (file: FileType) => {
    setIsRemoving(true);
    setTimeout(() => {
      removeFileFromContexts(file?.id);
    }, 100);
  };

  return (
    <div
      data-testid="file-chit"
      className={classNames([
        'file-chit',
        {
          error: fileHasError,
          editable: isEditable,
          'is-removing': isRemoving,
        },
      ])}
    >
      {isFileProcessingComplete() && <FileChitIcon type={extension} />}

      {fileIsUploading && (
        <div data-testid="file-is-uploading" className="file-icon uploading">
          <Progress
            type="circle"
            percent={30}
            size={25}
            trailColor="rgba(0, 0, 0, 0.0)"
            strokeColor="rgba(48, 209, 88, 1)"
            strokeWidth={20}
            showInfo={false}
          />
        </div>
      )}

      {fileIsProcessing && (
        <div data-testid="file-in-process" className="file-icon processing">
          <Spin size={'small'} />
        </div>
      )}

      <div className="file-information">
        <div className="file-name">
          {file.displayFileName || 'Missing File Name'}
        </div>
        <span className="file-description">{getStatusLabel(fileType)}</span>
      </div>

      {fileIsEditable && (
        <button
          data-testid="file-chit-close"
          className="file-chit-close"
          onClick={() => {
            removeChitFromFilesSelected(file);
          }}
        >
          <FileChitIconClose />
        </button>
      )}
    </div>
  );
};

export const FileChitIcon = ({ type }) => {
  const FileIconMapping = {
    pptx: FileChitIconPPTX,
    docx: FileChitIconDOCX,
    pdf: FileChitIconPDF,
    txt: FileChitIconTXT,
    xlsx: FileChitIconXLSX,
  };

  const FileIconComponent = FileIconMapping[type] || FileIconMapping.txt;
  const className = FileIconMapping[type] ? type : 'txt';

  return (
    <div data-testid="file-icon" className={`file-icon ${className}`}>
      <FileIconComponent />
    </div>
  );
};
